import type {RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';
import React from 'react';

import type {AxiosError} from 'axios';
import {useIntl} from 'react-intl';

import {OtpEntryForm} from '@edume/magnificent';

import MessageBubbleHeader from '../../components/Login/MessageBubbleHeader';
import LoginDisplay from '../../components/LoginDisplay';
import edumeClient from '../../edumeClient';
import useBreakpoint from '../../hooks/useBreakpoint';
import {useDispatch, useSelector} from '../../state/hooks';
import {
  MANAGER_LOGGED_IN,
  OTP_ERROR,
  RESET_OTP_ERROR,
} from '../../state/managerInsights/managerInsightsActionTypes';
import SimpleMessageView from '../landing/SimpleMessageView';

const OTP_LENGTH = 6;

export function OtpView({
  managerId,
  insightId,
}: {
  managerId: number;
  insightId: string;
}) {
  const [otp, setOtp] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [contactMethod, setContactMethod] = useState<string>('email');
  const [retries, setRetries] = useState<number>(0);
  const tooltipRef = useRef(null) as RefObject<HTMLDivElement>;
  const dispatch = useDispatch();
  const otpError = useSelector<string>((state) =>
    state.managerInsights.get('otpError')
  );
  const intl = useIntl();
  const {isSmallScreen} = useBreakpoint(960);

  const formattedContactMethod = intl.formatMessage({
    id: `OtpLogin.${contactMethod}`,
  });
  const content = {
    welcome: intl.formatMessage({id: 'OtpLogin.welcome'}, {firstName}),
    intro: intl.formatMessage({id: 'OtpLogin.intro'}),
    instruction: intl.formatMessage(
      {id: 'OtpLogin.instruction'},
      {contactMethod: formattedContactMethod}
    ),
    error: {
      expiredToken: intl.formatMessage({id: 'Otp.error.expiredToken'}),
      generic: intl.formatMessage({id: 'Otp.error.generic'}),
    },
    inputTitle: intl.formatMessage({id: 'OtpLogin.inputTitle'}),
    additionalCopy: intl.formatMessage({id: 'OtpLogin.additionalCopy'}),
    editContact: intl.formatMessage({id: 'OtpLogin.editContact'}),
    retry: intl.formatMessage({id: `OtpLogin.retry.send`}),
    alternative: intl.formatMessage({
      id: `OtpLogin.alternative.${contactMethod}`,
    }),
    or: intl.formatMessage({id: 'OtpLogin.or'}),
    tooltip: intl.formatMessage({id: 'OtpLogin.retry.tooltip'}),
  };

  useEffect(() => {
    async function requestOtp() {
      const response = await edumeClient.post('/managerInsights/sendOtp', {
        managerId,
        insightId,
      });
      setFirstName(response.data.firstName);
      setContactMethod(response.data.contactMethod);
    }
    requestOtp();
  }, [managerId, insightId, retries]);

  async function handleSubmit(newValue: string) {
    try {
      await edumeClient.post('/managerInsights/checkOtp', {
        otp: newValue,
        managerId: managerId,
      });
      dispatch({
        type: MANAGER_LOGGED_IN,
      });
    } catch (error) {
      const err = error as AxiosError;
      const errorCode = err?.response?.data.errorCode;
      console.log('error', errorCode);
      dispatch({
        type: OTP_ERROR,
        payload: {otpError: errorCode},
      });
    }
  }

  function otpContent() {
    return (
      <>
        <OtpEntryForm
          contactDetails={{email: ''}}
          contactType={'email'}
          onChange={(value) => {
            setOtp(value);
            if (value.trim().length === OTP_LENGTH) {
              handleSubmit(value);
            }
          }}
          otpError={otpError}
          otp={otp}
          content={content}
          onRetry={() => {
            setOtp('');
            dispatch({
              type: RESET_OTP_ERROR,
            });
            setRetries(retries + 1);
          }}
          tooltipRef={tooltipRef}
        />
      </>
    );
  }

  return (
    <LoginDisplay>
      <SimpleMessageView darkBackground={isSmallScreen} isMainLandmark={false}>
        <MessageBubbleHeader />
        <div
          style={{
            margin: 20,
          }}
        >
          {otpContent()}
        </div>
      </SimpleMessageView>
    </LoginDisplay>
  );
}
